import { useState, useEffect, useCallback, useMemo } from 'react';
import { s, authHeaders, Toast } from './listPageStyles';

const SUMMARY_URL = '/api/field-dashboard/summary';
const APPROVAL_BASE = '/api/approval-requests';

const REQUEST_TYPE_LABELS = {
  NON_ROUTE_VISIT: 'Rota Dışı Ziyaret',
  POINT_OPENING: 'Yeni Nokta Açma',
  VEHICLE_COUNT: 'Araç Sayım',
};

function pick(obj, ...keys) {
  if (!obj) return undefined;
  for (const k of keys) {
    if (obj[k] !== undefined && obj[k] !== null) return obj[k];
  }
  return undefined;
}

function unwrap(payload) {
  return pick(payload, 'Data', 'data') ?? payload ?? {};
}

function money(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '₺0';
  return '₺' + n.toLocaleString('tr-TR', { maximumFractionDigits: 0 });
}

function formatDate(value) {
  if (!value) return '-';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '-';
    return d.toLocaleString('tr-TR', {
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch { return '-'; }
}

function requestTypeLabel(t) {
  if (!t) return '-';
  return REQUEST_TYPE_LABELS[t] || t;
}

// Hızlı aralık → [fromISO, toISO]. 'all' → filtre yok.
function rangeToParams(key) {
  if (key === 'all') return { from: '', to: '' };
  const now = new Date();
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const to = new Date(todayStart); to.setDate(to.getDate() + 1);
  const from = new Date(todayStart);
  if (key === 'week') from.setDate(from.getDate() - 6);
  else if (key === 'month') from.setDate(from.getDate() - 29);
  return { from: from.toISOString(), to: to.toISOString() };
}

const RANGE_TABS = [
  { key: 'today', label: 'Bugün' },
  { key: 'week', label: 'Son 7 Gün' },
  { key: 'month', label: 'Son 30 Gün' },
  { key: 'all', label: 'Tümü' },
];

function KpiCard({ label, value, sub, tone }) {
  const tones = {
    blue: { color: '#1d4ed8', dot: 'var(--accent)', bg: 'rgba(0,115,234,0.06)', border: 'rgba(0,115,234,0.22)' },
    green: { color: '#0f5132', dot: '#16a34a', bg: 'rgba(0,200,117,0.07)', border: 'rgba(0,200,117,0.24)' },
    purple: { color: '#5b21b6', dot: '#8b5cf6', bg: 'rgba(139,92,246,0.07)', border: 'rgba(139,92,246,0.24)' },
    orange: { color: '#92400e', dot: 'var(--warn)', bg: 'rgba(253,171,61,0.08)', border: 'rgba(253,171,61,0.26)' },
  };
  const t = tones[tone] || tones.blue;
  return (
    <div style={{
      flex: '1 1 200px', minWidth: 190,
      border: `1px solid ${t.border}`, borderRadius: 14, padding: 16, background: t.bg,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12.5, fontWeight: 700, color: t.color }}>
        <span style={{ width: 7, height: 7, borderRadius: 999, background: t.dot }} />
        {label}
      </div>
      <div style={{ fontWeight: 900, fontSize: 28, color: '#0f172a', marginTop: 8, lineHeight: 1.1 }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4, fontWeight: 600 }}>{sub}</div>}
    </div>
  );
}

function ProgressBar({ done, total, color = '#16a34a' }) {
  const pct = total > 0 ? Math.min(100, Math.round((done / total) * 100)) : 0;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 120 }}>
      <div style={{ flex: 1, height: 6, borderRadius: 999, background: '#eef2f7', overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: 999 }} />
      </div>
      <span style={{ fontSize: 12, fontWeight: 700, color: '#475569', whiteSpace: 'nowrap' }}>{done}/{total}</span>
    </div>
  );
}

export function ChiefDashboardPage() {
  const [range, setRange] = useState('today');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [rejectRow, setRejectRow] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  const showToast = useCallback((type, message) => {
    setToast({ type, message });
    window.setTimeout(() => setToast(null), 3000);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { from, to } = rangeToParams(range);
      const params = new URLSearchParams();
      if (from) params.set('from', from);
      if (to) params.set('to', to);
      const qs = params.toString();
      const res = await fetch(`${SUMMARY_URL}${qs ? `?${qs}` : ''}`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`Özet alınamadı (${res.status})`);
      const payload = await res.json();
      setData(unwrap(payload));
    } catch (err) {
      showToast('error', err.message || 'Özet alınamadı');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [range, showToast]);

  useEffect(() => { load(); }, [load]);

  const approve = async (row) => {
    const id = pick(row, 'id', 'Id');
    setBusyId(id);
    try {
      const res = await fetch(`${APPROVAL_BASE}/${id}/approve`, { method: 'POST', headers: authHeaders() });
      if (!res.ok) throw new Error(`Onaylanamadı (${res.status})`);
      showToast('success', 'Talep onaylandı.');
      await load();
    } catch (err) {
      showToast('error', err.message || 'Onaylanamadı');
    } finally {
      setBusyId(null);
    }
  };

  const submitReject = async () => {
    if (!rejectRow) return;
    const id = pick(rejectRow, 'id', 'Id');
    setBusyId(id);
    try {
      const res = await fetch(`${APPROVAL_BASE}/${id}/reject`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: rejectReason.trim() || null }),
      });
      if (!res.ok) throw new Error(`Reddedilemedi (${res.status})`);
      showToast('success', 'Talep reddedildi.');
      setRejectRow(null);
      setRejectReason('');
      await load();
    } catch (err) {
      showToast('error', err.message || 'Reddedilemedi');
    } finally {
      setBusyId(null);
    }
  };

  const kpi = useMemo(() => ({
    completionPct: pick(data, 'completionPct', 'CompletionPct') ?? 0,
    visitedStops: pick(data, 'visitedStops', 'VisitedStops') ?? 0,
    plannedStops: pick(data, 'plannedStops', 'PlannedStops') ?? 0,
    visitCount: pick(data, 'visitCount', 'VisitCount') ?? 0,
    completedVisitCount: pick(data, 'completedVisitCount', 'CompletedVisitCount') ?? 0,
    orderCount: pick(data, 'orderCount', 'OrderCount') ?? 0,
    orderTotal: pick(data, 'orderTotal', 'OrderTotal') ?? 0,
    collectionTotal: pick(data, 'collectionTotal', 'CollectionTotal') ?? 0,
    pendingApprovalCount: pick(data, 'pendingApprovalCount', 'PendingApprovalCount') ?? 0,
  }), [data]);

  const pendingApprovals = pick(data, 'pendingApprovals', 'PendingApprovals') ?? [];
  const team = pick(data, 'team', 'Team') ?? [];

  return (
    <div style={s.container}>
      <div style={s.pageHeader}>
        <div style={s.pageHeaderText}>
          <h1 style={s.pageTitle}>Saha Şefi Paneli</h1>
          <p style={s.pageSubtitle}>
            Ekibin saha performansı — ziyaret tamamlama, sipariş, tahsilat ve bekleyen
            onaylar. Onayları doğrudan buradan işleyebilirsin. (Mobil ChiefDashboard muadili.)
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          {RANGE_TABS.map(tab => (
            <button
              key={tab.key}
              type="button"
              onClick={() => setRange(tab.key)}
              style={{ ...(range === tab.key ? s.btnPrimary : s.btnSecondary), padding: '6px 14px', fontSize: 13 }}
            >
              {tab.label}
            </button>
          ))}
          <button type="button" style={s.btnSecondary} onClick={load} disabled={loading}>
            {loading ? 'Yükleniyor...' : 'Yenile'}
          </button>
        </div>
      </div>

      {/* KPI kartları */}
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 18 }}>
        <KpiCard tone="green" label="Ziyaret Tamamlama" value={`%${kpi.completionPct}`}
          sub={kpi.plannedStops > 0 ? `${kpi.visitedStops} / ${kpi.plannedStops} durak` : `${kpi.completedVisitCount} / ${kpi.visitCount} ziyaret`} />
        <KpiCard tone="blue" label="Sipariş" value={kpi.orderCount}
          sub={money(kpi.orderTotal)} />
        <KpiCard tone="purple" label="Tahsilat" value={money(kpi.collectionTotal)}
          sub="dönem toplamı" />
        <KpiCard tone="orange" label="Bekleyen Onay" value={kpi.pendingApprovalCount}
          sub="anlık (PENDING_CHIEF)" />
      </div>

      {/* Bekleyen onaylar */}
      <div style={{ marginBottom: 20 }}>
        <h2 style={{ fontSize: 16, fontWeight: 800, color: '#0f172a', margin: '0 0 10px' }}>
          Bekleyen Onaylar ({pendingApprovals.length})
        </h2>
        {pendingApprovals.length === 0 ? (
          <div style={{ padding: 24, textAlign: 'center', color: '#94a3b8', border: '1px dashed #e5e7eb', borderRadius: 12 }}>
            Bekleyen onay yok 🎉
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(360px, 1fr))', gap: 12 }}>
            {pendingApprovals.map((row, idx) => {
              const id = pick(row, 'id', 'Id');
              const lat = pick(row, 'gpsLatitude', 'GpsLatitude');
              const lng = pick(row, 'gpsLongitude', 'GpsLongitude');
              const busy = busyId === id;
              return (
                <div key={id || idx} style={{ border: '1px solid #e5e7eb', borderRadius: 12, padding: 14, background: '#fff' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                    <div>
                      <div style={{ fontWeight: 800, fontSize: 14, color: '#0f172a' }}>{requestTypeLabel(pick(row, 'requestType', 'RequestType'))}</div>
                      <div style={{ fontSize: 12.5, color: '#6b7280', marginTop: 2 }}>{pick(row, 'requesterName', 'RequesterName') || 'Bilinmeyen temsilci'}</div>
                    </div>
                    {pick(row, 'trackingNo', 'TrackingNo') && (
                      <span style={{ fontSize: 11, color: '#94a3b8', fontFamily: 'monospace' }}>{pick(row, 'trackingNo', 'TrackingNo')}</span>
                    )}
                  </div>
                  {pick(row, 'notes', 'Notes') && (
                    <div style={{ fontSize: 12.5, color: '#475569', marginTop: 8, lineHeight: 1.4 }}>{pick(row, 'notes', 'Notes')}</div>
                  )}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 8, fontSize: 11.5, color: '#94a3b8' }}>
                    <span>{formatDate(pick(row, 'createdDate', 'CreatedDate'))}</span>
                    {lat != null && lng != null && (
                      <a href={`https://maps.google.com/?q=${lat},${lng}`} target="_blank" rel="noreferrer" style={{ color: 'var(--accent)', fontWeight: 600 }}>
                        📍 Harita
                      </a>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                    <button
                      type="button"
                      onClick={() => approve(row)}
                      disabled={busy}
                      style={{ ...s.btnPrimary, flex: 1, padding: '8px 0', background: '#16a34a', borderColor: '#16a34a', opacity: busy ? 0.6 : 1 }}
                    >
                      {busy ? '...' : 'Onayla'}
                    </button>
                    <button
                      type="button"
                      onClick={() => { setRejectRow(row); setRejectReason(''); }}
                      disabled={busy}
                      style={{ ...s.btnSecondary, flex: 1, padding: '8px 0', color: '#dc2626', borderColor: 'rgba(220,38,38,0.35)' }}
                    >
                      Reddet
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Ekip performansı */}
      <div>
        <h2 style={{ fontSize: 16, fontWeight: 800, color: '#0f172a', margin: '0 0 10px' }}>
          Ekip Performansı ({team.length})
        </h2>
        <div style={{ border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead style={{ background: '#f9fafb' }}>
              <tr>
                <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 700, fontSize: 11, color: '#6b7280' }}>Temsilci</th>
                <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 700, fontSize: 11, color: '#6b7280', width: 180 }}>Ziyaret İlerleme</th>
                <th style={{ padding: '10px 14px', textAlign: 'right', fontWeight: 700, fontSize: 11, color: '#6b7280' }}>Sipariş</th>
                <th style={{ padding: '10px 14px', textAlign: 'right', fontWeight: 700, fontSize: 11, color: '#6b7280' }}>Ciro</th>
              </tr>
            </thead>
            <tbody>
              {team.length === 0 && (
                <tr><td colSpan="4" style={{ padding: 24, textAlign: 'center', color: '#94a3b8' }}>Bu dönemde aktivite yok</td></tr>
              )}
              {team.map((t, idx) => {
                const planned = pick(t, 'plannedStops', 'PlannedStops') ?? 0;
                const visited = pick(t, 'visitedStops', 'VisitedStops') ?? 0;
                const vc = pick(t, 'visitCount', 'VisitCount') ?? 0;
                const cvc = pick(t, 'completedVisitCount', 'CompletedVisitCount') ?? 0;
                const useStops = planned > 0;
                return (
                  <tr key={pick(t, 'representativeId', 'RepresentativeId') || idx} style={{ borderTop: idx > 0 ? '1px solid #f3f4f6' : 'none' }}>
                    <td style={{ padding: '10px 14px', fontWeight: 600 }}>{pick(t, 'representativeName', 'RepresentativeName') || '-'}</td>
                    <td style={{ padding: '10px 14px' }}>
                      <ProgressBar done={useStops ? visited : cvc} total={useStops ? planned : vc} />
                    </td>
                    <td style={{ padding: '10px 14px', textAlign: 'right', fontWeight: 600 }}>{pick(t, 'orderCount', 'OrderCount') ?? 0}</td>
                    <td style={{ padding: '10px 14px', textAlign: 'right', fontWeight: 700, color: '#0f172a' }}>{money(pick(t, 'orderTotal', 'OrderTotal'))}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Reddet modal */}
      {rejectRow && (
        <div style={s.overlay} onClick={() => setRejectRow(null)}>
          <div style={s.modal} onClick={(e) => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <h2 style={s.modalTitle}>Talebi Reddet</h2>
              <button type="button" style={s.closeBtn} onClick={() => setRejectRow(null)}>×</button>
            </div>
            <div style={s.modalBody}>
              <div style={{ fontSize: 13, color: '#475569', marginBottom: 10 }}>
                <strong>{requestTypeLabel(pick(rejectRow, 'requestType', 'RequestType'))}</strong>
                {' — '}{pick(rejectRow, 'requesterName', 'RequesterName') || 'temsilci'}
              </div>
              <div style={s.formGroup}>
                <label style={s.label}>Red Gerekçesi (opsiyonel)</label>
                <textarea
                  rows={3}
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder="Reddetme sebebi..."
                  style={s.textarea}
                />
              </div>
            </div>
            <div style={s.modalFooter}>
              <button type="button" style={s.btnSecondary} onClick={() => setRejectRow(null)} disabled={busyId}>
                Vazgeç
              </button>
              <button
                type="button"
                style={{ ...s.btnDanger, opacity: busyId ? 0.6 : 1 }}
                onClick={submitReject}
                disabled={busyId}
              >
                {busyId ? 'Reddediliyor...' : 'Reddet'}
              </button>
            </div>
          </div>
        </div>
      )}

      <Toast toast={toast} />
    </div>
  );
}

export default ChiefDashboardPage;
